import { makeResetStyles, mergeClasses, makeStyles } from '@fluentui/react-components';
import { tokens, typographyStyles } from '@fluentui-copilot/tokens';
import type { UserMessageSlots, UserMessageState } from './UserMessage.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const userMessageClassNames: SlotClassNames<UserMessageSlots> = {
  root: 'fai-UserMessage',
  accessibleHeading: 'fai-UserMessage__accessibleHeading',
  timestamp: 'fai-UserMessage__timestamp',
  actionBarAccessibleButton: 'fai-UserMessage__actionBarAccessibleButton',
  actionBar: 'fai-UserMessage__actionBar',
  message: 'fai-UserMessage__message',
};

/**
 * Styles for the root slot
 */
const useRootClassName = makeResetStyles({
  position: 'relative',
  display: 'grid',
  gridAutoRows: 'max-content',
  gridAutoFlow: 'column',
  alignSelf: 'flex-end',
  gridTemplateAreas: `"start timestamp"
                      "button  button"
                      "message message"`,
  color: tokens.colorNeutralForeground1,
  columnGap: tokens.spacingHorizontalS,

  padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
  borderRadius: tokens.borderRadiusXLarge,

  [`&:focus`]: {
    outline: 'none',
  },
  [`&:focus .${userMessageClassNames.message}`]: {
    outline: `${tokens.strokeWidthThick} solid ${tokens.colorNeutralForeground1}`,
  },
  [`&:focus:not(:focus-visible) .${userMessageClassNames.message}`]: {
    outline: 'none',
  },
});

/**
 * Styles for the hidden heading slot
 */
const useHeadingStyles = makeResetStyles({
  clip: 'rect(0px, 0px, 0px, 0px)',
  gridArea: 'start',
  height: '1px',
  margin: '-1px',
  width: '1px',
  position: 'absolute',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

/**
 * Styles for the name slot
 */
const useTimestampStyles = makeResetStyles({
  color: tokens.colorNeutralForeground3,
  gridArea: 'timestamp',
  ...typographyStyles.caption1,
});

/**
 * Styles for the accessible button slot
 */
const useActionBarAccessibleButtonStyles = makeResetStyles({
  gridArea: 'button',
  clip: 'rect(0px, 0px, 0px, 0px)',
  height: '1px',
  margin: '-1px',
  width: '1px',
  position: 'absolute',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

/**
 * Styles for the action bar slot
 */
const useActionBarStyles = makeStyles({
  root: {
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    borderRadius: tokens.borderRadiusMedium,

    // Adding a z-index to ensure the action bar appears above overlapping content.
    // The action bar needs to render inline for tabster to handle the tab loop that includes a mix of portal and inline content.
    // Thus we use z-index as a workaround to ensure the action bar is above any content that may overlap it.
    zIndex: 2000,
  },
});

/**
 * Styles for the message slot
 */
const useMessageStyles = makeResetStyles({
  gridArea: 'message',
  backgroundColor: tokens.colorNeutralBackground3,
  paddingTop: tokens.spacingVerticalS,
  paddingBottom: tokens.spacingVerticalS,
  paddingLeft: tokens.spacingHorizontalL,
  paddingRight: tokens.spacingHorizontalL,
  borderRadius: tokens.borderRadiusXLarge,
  border: `${tokens.strokeWidthThin} solid ${tokens.colorTransparentStroke}`,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
});

/**
 * Apply styling to the UserMessage slots based on the state
 */
export const useUserMessageStyles_unstable = (state: UserMessageState): UserMessageState => {
  'use no memo';

  const rootClassName = useRootClassName();
  const headingStyles = useHeadingStyles();
  const timestampStyles = useTimestampStyles();
  const actionBarAccessibleButtonStyles = useActionBarAccessibleButtonStyles();
  const actionBarStyles = useActionBarStyles();
  const messageStyles = useMessageStyles();

  state.root.className = mergeClasses(userMessageClassNames.root, rootClassName, state.root.className);

  if (state.accessibleHeading) {
    state.accessibleHeading.className = mergeClasses(
      userMessageClassNames.accessibleHeading,
      headingStyles,
      state.accessibleHeading.className,
    );
  }

  if (state.timestamp) {
    state.timestamp.className = mergeClasses(
      userMessageClassNames.timestamp,
      timestampStyles,
      state.timestamp.className,
    );
  }

  if (state.actionBarAccessibleButton) {
    state.actionBarAccessibleButton.className = mergeClasses(
      userMessageClassNames.actionBarAccessibleButton,
      actionBarAccessibleButtonStyles,
      state.actionBarAccessibleButton.className,
    );
  }

  if (state.actionBar) {
    state.actionBar.className = mergeClasses(
      userMessageClassNames.actionBar,
      actionBarStyles.root,
      state.actionBar.className,
    );
  }

  state.message.className = mergeClasses(userMessageClassNames.message, messageStyles, state.message.className);

  return state;
};
